/**
 * Bridge from shared tool contracts to this package's harness tools.
 *
 * Contracts are zod-version-neutral DATA (`@project/shared/tool-contracts`);
 * this package compiles them with its OWN zod (v4 — pinned by the Claude
 * Agent SDK's `zod: ^4` peer dependency, see package-metadata.test.ts) so
 * every schema object in the harness is a genuine same-version zod v4
 * instance. Handler inputs are typed from the contract data (`InferShape`)
 * rather than through zod's object inference, which hits TS2589 on generic
 * mapped shapes.
 */
import { z } from "zod";
import {
  compileShape,
  type FieldMap,
  type InferField,
  type InferShape,
  type OptionalSpec,
  type ToolContract,
  type ToolSurface,
} from "@project/shared/tool-contracts";
import type { HarnessToolAnnotations, HarnessToolDefinition } from "../harness/index.js";

/** Precise zod-v4 shape type for a contract surface (optional specs → ZodOptional keys). */
export type AgentContractShape<F extends FieldMap> = {
  [K in keyof F]: F[K] extends OptionalSpec<infer I>
    ? z.ZodOptional<z.ZodType<InferField<I>>>
    : z.ZodType<InferField<F[K]>>;
};

export function agentShape<F extends FieldMap>(surface: ToolSurface<F>): AgentContractShape<F> {
  return compileShape(z, surface.fields) as AgentContractShape<F>;
}

/**
 * `defineTool`, sourced from a shared contract's agent surface. Mirrors the
 * `defineTool` wrapper in harness/types.ts, but types the handler input from
 * the contract's field specs.
 */
export function defineContractTool<A extends FieldMap>(
  contract: ToolContract<A, FieldMap>,
  handler: (
    input: InferShape<A>,
  ) => Promise<{ content: { type: string; text?: string; data?: string; mimeType?: string }[] }>,
  options?: { annotations?: HarnessToolAnnotations; strict?: boolean },
): HarnessToolDefinition {
  return {
    name: contract.name,
    description: contract.agent.description,
    schema: agentShape(contract.agent) as unknown as z.ZodRawShape,
    handler: handler as HarnessToolDefinition["handler"],
    annotations: options?.annotations,
    strict: options?.strict,
  };
}
